// Footer Component - AgentBheem ERP
// Uses same dark theme as Sidebar
import { cn } from '@/lib/utils'
import { Heart, Wifi, WifiOff, Sparkles } from 'lucide-react'
import { useState, useEffect } from 'react'

export function Footer() {
  const year = new Date().getFullYear()
  const [isOnline, setIsOnline] = useState(true)

  // Monitor connection status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    setIsOnline(navigator.onLine)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return (
    <footer
      className={cn(
        'h-14 flex-shrink-0',
        'bg-[var(--sidebar-bg)]',
        'border-t border-[var(--sidebar-border)]'
      )}
    >
      <div className="flex h-full items-center justify-between px-4 lg:px-6">
        {/* Left Section - Copyright */}
        <div className="flex items-center gap-3">
          {/* Mini Logo */}
          <div className="hidden sm:flex">
            <img
              src="/logo.png"
              alt="AgentBheem"
              className="h-7 w-auto object-contain"
            />
          </div>

          <div className="flex flex-col">
            <p className="text-xs text-[var(--sidebar-text-hover)] font-medium">
              &copy; {year} AgentBheem
            </p>
            <p className="hidden sm:flex items-center gap-1 text-[10px] text-[var(--sidebar-text)]">
              Made with <Heart className="h-2.5 w-2.5 text-red-400 fill-red-400 animate-pulse" /> in India
            </p>
          </div>
        </div>

        {/* Center Section - Quick Links (hidden on mobile) */}
        <div className="hidden lg:flex items-center gap-1">
          <FooterLink href="#">Docs</FooterLink>
          <span className="text-[var(--sidebar-text)]">·</span>
          <FooterLink href="#">Support</FooterLink>
          <span className="text-[var(--sidebar-text)]">·</span>
          <FooterLink href="#">Privacy</FooterLink>
        </div>

        {/* Right Section - Status & Version */}
        <div className="flex items-center gap-3">
          {/* Connection Status */}
          <div
            className={cn(
              'flex items-center gap-2 px-2.5 py-1.5 rounded',
              isOnline
                ? 'bg-[var(--sidebar-accent-green)]/10 text-[var(--sidebar-accent-green)]'
                : 'bg-red-500/10 text-red-400'
            )}
          >
            {isOnline ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--sidebar-accent-green)] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--sidebar-accent-green)]" />
                </span>
                <Wifi className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-[11px] font-medium">Connected</span>
              </>
            ) : (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
                <WifiOff className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-[11px] font-medium">Offline</span>
              </>
            )}
          </div>

          {/* Version Badge */}
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-[var(--sidebar-bg-hover)] border border-[var(--sidebar-border)]">
            <span className="text-[11px] text-[var(--sidebar-text)] font-medium">v1.0.0</span>
          </div>

          {/* Environment Badge */}
          {import.meta.env.DEV && (
            <div className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded',
              'bg-gradient-to-r from-[var(--sidebar-accent)]/20 to-[var(--sidebar-accent-cyan)]/10',
              'border border-[var(--sidebar-accent)]/20'
            )}>
              <Sparkles className="h-3 w-3 text-[var(--sidebar-accent)]" />
              <span className="text-[11px] text-[var(--sidebar-accent)] font-semibold tracking-wide">DEV</span>
            </div>
          )}
        </div>
      </div>
    </footer>
  )
}

// Footer Link Component
function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className={cn(
        'px-2 py-1 text-[11px] text-[var(--sidebar-text)] font-medium rounded-md',
        'hover:text-[var(--sidebar-accent)] hover:bg-[var(--sidebar-accent)]/10',
        'transition-all duration-200'
      )}
    >
      {children}
    </a>
  )
}
